import React, { useState, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../Redux/store";
import PercentIcon from "@mui/icons-material/Percent";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Typography, IconButton } from "@mui/material";
import { saveTitle } from "../Database/saved-db";
import { useLocation, useNavigate } from "react-router-dom";
import { updateTitle } from "../Database/saved-db";
import { setCurrencySymbol } from "../Redux/currencySlice";
import SaveTitleDialog from "../dialog-box/SaveTitleDialog";
import Slider from "@mui/material/Slider";
import { Helmet } from "react-helmet";
import LumpSumPDFGenerator from "../pdf/pdf-lumpsum";
import ReactGA from "react-ga4";
import BootstrapTooltip from "../components/tooltip";
import SavedSnackbar from "../utils/saved-snackbar";

Chart.register(ArcElement, Tooltip, Legend);

const LumpSumCalculator: React.FC = () => {
  const defaultPrincipal = "1,00,000";
  const defaultRate = "8";
  const defaultPeriod = "5";
  const [icon, setIcon] = useState<React.ReactNode>(<FavoriteBorderIcon />);

  const location = useLocation();
  const navigate = useNavigate();

  const { calculation } = location.state || {};

  const [principal, setPrincipal] = useState(
    calculation?.principal?.toString() || defaultPrincipal
  );
  const [rate, setRate] = useState(
    calculation?.rate?.toString() || defaultRate
  );
  const [period, setPeriod] = useState(
    calculation?.period?.toString() || defaultPeriod
  );
  const [finalAmount, setFinalAmount] = useState<number | null>(null);
  const [totalInterest, setTotalInterest] = useState<number | null>(null);
  const [error, setError] = useState({
    principal: "",
    rate: "",
    period: "",
  });
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [calculatedPrincipal, setCalculatedPrincipal] = useState(0);
  const dispatch = useDispatch();
  const currencySymbol = useSelector(
    (state: RootState) => state.country.currencySymbol
  );
  const [symbol, setSymbol] = useState(currencySymbol);
  const [principalForChart, setPrincipalForChart] = useState(0);
  const [interestForChart, setInterestForChart] = useState(0);
  const [savedTitle, setSavedTitle] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isCalculated, setIsCalculated] = useState(false);

  const formatNumberWithCommas = (value: number | string) => {
    return new Intl.NumberFormat("en-IN").format(Number(value));
  };

  const calculateValues = () => {
    const principalValue = parseFloat(principal.replace(/,/g, ""));
    const rateValue = parseFloat(rate) / 100;
    const periodValue = parseFloat(period);

    if (!isNaN(principalValue) && !isNaN(rateValue) && !isNaN(periodValue)) {
      const futureValue = principalValue * Math.pow(1 + rateValue, periodValue);
      const interestEarned = futureValue - principalValue;
      setCurrencySymbol(calculation.currencySymbol.toString());
      setFinalAmount(futureValue);
      setTotalInterest(interestEarned);
    }
  };

  const handleReset = () => {
    ReactGA.event({
      category: "Lumpsum calculator",
      action: "Lumpsum Reset Button",
      label: "Lumpsum Calculator",
    });
    setPrincipal("1,00,000");
    setRate("8");
    setPeriod("5");
    setFinalAmount(null);
    setTotalInterest(null);
    setError({ principal: "", rate: "", period: "" });
    setShowPdfButton(false);
  };
  useEffect(() => {
    if (principal && rate && period) {
    }
  }, []);

  const calculateDefaultValues = () => {
    const principalValue = parseFloat(defaultPrincipal.replace(/,/g, ""));
    const rateValue = parseFloat(defaultRate) / 100;
    const periodValue = parseFloat(defaultPeriod);

    const futureValue = principalValue * Math.pow(1 + rateValue, periodValue);
    const interestEarned = futureValue - principalValue;
    setFinalAmount(futureValue);
    setTotalInterest(interestEarned);
    setPrincipalForChart(principalValue);
    setInterestForChart(interestEarned);
    setShowPdfButton(true);
  };

  const handleCalculate = () => {
    ReactGA.event({
      category: "Lumpsum calculator",
      action: "Lumpsum Calculate Button",
      label: "Lumpsum Calculator",
    });
    let hasError = false;

    const principalValue = parseFloat(principal.replace(/,/g, ""));
    const rateValue = parseFloat(rate) / 100;
    const periodValue = parseFloat(period);

    if (isNaN(principalValue) || principalValue < 1000) {
      setError((prevError) => ({
        ...prevError,
        principal: "Principal Amount can't be less than ₹1000.",
      }));
      hasError = true;
    } else {
      setError((prevError) => ({
        ...prevError,
        principal: "",
      }));
    }

    if (isNaN(rateValue) || rateValue < 0.01) {
      setError((prevError) => ({
        ...prevError,
        rate: "Expected Annual Return Rate must be at least 1%.",
      }));
      hasError = true;
    } else {
      setError((prevError) => ({
        ...prevError,
        rate: "",
      }));
    }

    if (isNaN(periodValue) || periodValue < 1) {
      setError((prevError) => ({
        ...prevError,
        period: "Investment Period must be at least 1 year.",
      }));
      hasError = true;
    } else {
      setError((prevError) => ({
        ...prevError,
        period: "",
      }));
    }

    if (hasError) return;

    const futureValue = principalValue * Math.pow(1 + rateValue, periodValue);
    const interestEarned = futureValue - principalValue;

    setFinalAmount(futureValue);
    setTotalInterest(interestEarned);
    setPrincipalForChart(principalValue);
    setInterestForChart(interestEarned);
    setShowPdfButton(true);
    setCalculatedPrincipal(principalValue);
    setIsCalculated(true);
  };

  const handlePrincipalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/,/g, "").replace(/\D/g, "");
    const numericValue = parseFloat(value);

    if (value === "0") {
      setError((prevError) => ({
        ...prevError,
        principal: "Principal cannot be zero.",
      }));
      setPrincipal("");
    } else if (value.length > 8) {
      setError((prevError) => ({
        ...prevError,
        principal: "Principal amount cannot exceed 8 digits.",
      }));
    } else if (numericValue < 500 && value !== "") {
      setError((prevError) => ({
        ...prevError,
        principal: "Principal Amount can't be less than ₹500.",
      }));
      setPrincipal(value);
    } else {
      setError((prevError) => ({
        ...prevError,
        principal: "",
      }));
      setPrincipal(value === "" ? "" : formatNumberWithCommas(value));
    }
  };

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    const parts = value.split(".");

    if (parts.length > 2 || (parts.length === 2 && parts[1].length > 2)) {
      return;
    }

    const numericValue = parseFloat(value);
    if (value === "" || (numericValue >= 1 && numericValue <= 30)) {
      setRate(value);
      setError((prevError) => ({ ...prevError, rate: "" }));
    } else {
      setError((prevError) => ({
        ...prevError,
        rate: "Interest rate must be between 1 and 30.",
      }));
    }
  };

  const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    const numericValue = parseFloat(value);

    if (value === "" || (numericValue >= 1 && numericValue <= 50)) {
      setPeriod(value);
      setError((prevError) => ({ ...prevError, period: "" }));
    } else {
      setError((prevError) => ({
        ...prevError,
        period: "Years must be between 1 and 50.",
      }));
    }
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setTitle("");
  };

  const handleCancel = () => {
    handleDialogClose();
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: "timeout" | "clickaway" | "escapeKeyDown"
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };
  const handleSubmit = async () => {
    setError({ principal: "", rate: "", period: "" });

    if (principal.trim() === "") {
      setError((prevError) => ({
        ...prevError,
        principal: "Please fill in the Principal Amount.",
      }));
      return;
    }

    if (rate.trim() === "") {
      setError((prevError) => ({
        ...prevError,
        rate: "Please fill in the Expected Annual Return Rate.",
      }));
      return;
    }

    if (period.trim() === "") {
      setError((prevError) => ({
        ...prevError,
        period: "Please fill in the Investment Period.",
      }));
      return;
    }

    if (title.trim().length > 50) {
      alert("Title must be 50 characters or less.");
      return;
    }

    const updatedData = {
      title,
      path: "/lumpsum-calculator.html",
      principal: parseFloat(principal.replace(/,/g, "")),
      rate: parseFloat(rate),
      period: parseFloat(period),
      finalAmount,
      totalInterest,
      currencySymbol,
    };

    if (calculation?.id) {
      await updateTitle(calculation.id, updatedData);
    } else {
      await saveTitle(updatedData);
    }
    setSnackbarMessage("Saved successfully!");
    setSnackbarOpen(true);
    setIcon(<FavoriteIcon style={{ color: "red" }} />);
    handleDialogClose();
    setTitle("");
  };

  useEffect(() => {
    const { currencySymbol: passedCurrencySymbol } = location.state || {};
    if (passedCurrencySymbol) {
      dispatch(setCurrencySymbol(passedCurrencySymbol));
      setSymbol(passedCurrencySymbol);
    } else {
      setSymbol(currencySymbol);
    }

    if (calculation) {
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      setPrincipal(calculation.principal.toString());
      setRate(calculation.rate.toString());
      setPeriod(calculation.period.toString());
      setTitle(calculation.title);
      setCurrencySymbol(calculation.currencySymbol.toString());
      setSavedTitle(calculation.title || "");
      handleCalculate();
    } else {
      calculateDefaultValues();
    }
  }, [calculation]);

  const total = principalForChart + interestForChart
  const percentagePrincipal = Math.round((principalForChart / total) * 100)
  const percentageInterest = Math.round((interestForChart / total) * 100)

  const pieChartData = {
    labels: ["Principal Amount %", "Total Interest Earned %"],
    datasets: [
      {
        data: [percentagePrincipal, percentageInterest],
        backgroundColor: ["#4CAF50", "#FF9800"],
        hoverBackgroundColor: ["#66BB6A", "#FFB74D"],
      },
    ],
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCalculate();
    }
  };

  return (
    <>
      <Helmet>
        <title>Lumpsum Calculator</title>
        <meta
          name="description"
          content="Calculate your investment returns easily with our Lumpsum Calculator. Estimate the future value of your one-time investment based on the expected rate of return and investment duration."
        />
        <link rel="canonical" href="/lumpsum-calculator.html" />
      </Helmet>

      <SavedSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />
      <div className="container mt-5" onKeyPress={handleKeyPress}>
        <div className="row justify-content-center">
          <div className="d-flex justify-content-between align-items-center">
            <h5
              className="mb-2"
              style={{
                fontFamily: "serif",
                fontSize: "30px",
                fontWeight: "bold",
              }}
            >
              Lumpsum Calculator
            </h5>
          </div>

          <p>
            A Lumpsum Calculator helps estimate the future value of a one-time
            investment over a specific period, considering the expected rate of
            return.
          </p>
          <div className="col-12 mb-3">
            {savedTitle && (
              <Typography variant="h6" style={{ marginLeft: "20px" }}>
                {savedTitle}
              </Typography>
            )}
          </div>
          <div className="col-12 col-md-6 mb-4">
            <div
              className="card shadow-sm"
              style={{ width: "100%", height: "550px", margin: "0 auto" }}
            >
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="principal" className="form-label">
                    Principal Amount ({currencySymbol})
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="principal"
                    value={principal}
                    onChange={handlePrincipalChange}
                  />
                  {error.principal && (
                    <p className="text-danger">{error.principal}</p>
                  )}
                </div>
                <div className="mb-3 d-flex justify-content-center">
                  <Slider
                    min={500}
                    max={10000000}
                    step={1000}
                    value={parseFloat(principal.replace(/,/g, "")) || 100000}
                    onChange={(e, newValue) =>
                      setPrincipal(newValue.toString())
                    }
                    valueLabelDisplay="auto"
                    shiftStep={30}
                    sx={{ width: "95%" }}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="rate" className="form-label">
                    Expected Annual Return Rate (%)
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="rate"
                      value={rate}
                      onChange={handleRateChange}
                    />
                    <span className="input-group-text">
                      <PercentIcon />
                    </span>
                  </div>
                  {error.rate && <p className="text-danger">{error.rate}</p>}
                </div>

                <div className="mb-3">
                  <label htmlFor="period" className="form-label">
                    Investment Period (Years)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="period"
                    value={period}
                    onChange={handlePeriodChange}
                  />
                  {error.period && (
                    <p className="text-danger">{error.period}</p>
                  )}
                </div>

                <div
                  className="d-flex justify-content-center "
                  style={{ marginTop: "60px" }}
                >
                  <button
                    className="btn btn-primary btn-lg "
                    onClick={handleCalculate}
                  >
                    Calculate
                  </button>
                </div>
                <div className="d-flex justify-content-center">
                  <a
                    onClick={handleReset}
                    style={{ cursor: "pointer", textDecoration: "none" }}
                    className=" pt-2"
                  >
                    Reset
                  </a>
                </div>
              </div>
            </div>
          </div>
          {finalAmount !== null && totalInterest !== null && (
            <div className="col-12 col-md-6 mb-4">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title ">Result</h5>
                    <BootstrapTooltip title="Save Calculation" placement="top">
                      <IconButton color="secondary" onClick={handleDialogOpen}>
                        {icon}
                      </IconButton>
                    </BootstrapTooltip>
                  </div>
                  <p className="card-text d-flex justify-content-between">
                    <strong>Principal Amount:</strong>
                    <span className="text-end">
                      {currencySymbol}{" "}
                      {formatNumberWithCommas(principalForChart)}
                    </span>
                  </p>
                  <p className="card-text">
                    <strong>Total Amount:</strong>
                    <span className="float-end">
                      {currencySymbol}{" "}
                      {formatNumberWithCommas(
                        Math.ceil(finalAmount !== null ? finalAmount : 0)
                      )}
                    </span>
                  </p>
                  <p className="card-text">
                    <strong>Total Interest Earned: </strong>
                    <span className="float-end">
                      {currencySymbol}{" "}
                      {totalInterest !== null
                        ? formatNumberWithCommas(Math.ceil(totalInterest))
                        : "0"}
                    </span>
                  </p>
                </div>
              </div>

              {finalAmount && (
                <div className="card shadow-sm mt-4">
                  <div className="card-body">
                    <h5 className="card-title mb-4">Investment Breakdown</h5>
                    <div style={{ width: "100%", height: "270px" }}>
                      <Pie
                        data={pieChartData}
                        options={{ maintainAspectRatio: false }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div
            className="d-flex justify-content-end"
            style={{ marginTop: "45px", paddingLeft: "90px" }}
          >
            {showPdfButton && (
              <LumpSumPDFGenerator
                principal={principal}
                rate={rate}
                period={period}
                finalAmount={finalAmount}
                totalInterest={totalInterest}
                isCalculated={isCalculated}    
               />
            )}
          </div>
        </div>

        {/* Dialog for Title Input */}
        <SaveTitleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          title={title}
          setTitle={setTitle}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </>
  );
};

export default LumpSumCalculator;
